import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Axios instance with credentials
const http = axios.create({ baseURL: API, withCredentials: true });

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const formatDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState("login"); // 'login' | 'register'
  const [authIntent, setAuthIntent] = useState(null); // description shown in modal

  const checkAuth = useCallback(async () => {
    // If returning from OAuth callback, skip /me check — AuthCallback handles it
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await http.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await http.post("/auth/login", { email, password });
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await http.post("/auth/register", payload);
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await http.post("/auth/logout"); } catch (e) { console.warn("[auth] logout request failed", e); }
    setUser(null);
  };

  const updateProfile = async (payload) => {
    try {
      const { data } = await http.patch("/auth/me", payload);
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatDetail(e.response?.data?.detail) || e.message };
    }
  };

  const startGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleGoogleSession = async (sessionId) => {
    try {
      const { data } = await http.post("/auth/google/callback", { session_id: sessionId });
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatDetail(e.response?.data?.detail) || e.message };
    }
  };

  const requireAuth = (intent) => {
    if (user) return true;
    setAuthIntent(intent || null);
    setAuthModalMode("login");
    setAuthModalOpen(true);
    return false;
  };

  const openAuth = (mode = "login", intent = null) => {
    setAuthIntent(intent);
    setAuthModalMode(mode);
    setAuthModalOpen(true);
  };

  const closeAuth = () => {
    setAuthModalOpen(false);
    setAuthIntent(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user, loading, login, register, logout, updateProfile,
        startGoogleLogin, handleGoogleSession,
        authModalOpen, authModalMode, authIntent,
        openAuth, closeAuth, requireAuth, setAuthModalMode,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
